import type { AppDocsQuery } from '@paintscout/api'
import { AppDocsDocument, useUpdateUserMutation } from '@paintscout/api'
import type { TourEvent, TourStateAdapter } from '@paintscout/ui'
import { TourProvider } from '@paintscout/ui'
import { useUser } from '@paintscout/ui-business'
import * as Sentry from '@sentry/react'
import React, { useCallback, useMemo, useRef } from 'react'
import { track } from 'shared/util/analytics'

/**
 * Gives every product tour in the app its two cross-cutting pieces: completion state in the user's
 * preferences, and analytics through `track()`.
 *
 * Mount it once. A new tour is then persisted per user and instrumented because it sits in the
 * tree, not because somebody remembered to wire it up (ENG-1963, ENG-1965).
 *
 * `@paintscout/ui` cannot import from `apps/shared`, so the wiring is injected rather than done in
 * the component. That is why this file exists at all.
 */
export default function AppTourProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const { user, preferences } = useUser()
  const [updateUser] = useUpdateUserMutation()

  /*
    The adapter is handed to context, so it must not change identity on every render — a new object
    each time would restart each tour's auto-start timer. Reads go through a ref instead, which
    also means a write is never based on a stale copy of preferences.
  */
  const preferencesRef = useRef(preferences)
  preferencesRef.current = preferences
  const userIdRef = useRef(user?.user_id)
  userIdRef.current = user?.user_id

  const read = useCallback<TourStateAdapter['read']>((tourId) => {
    return preferencesRef.current?.tours?.[tourId] ?? null
  }, [])

  const write = useCallback<TourStateAdapter['write']>(
    (tourId, record) => {
      const currentPreferences = preferencesRef.current
      const userId = userIdRef.current
      // No user means no preferences document to write to. Nothing to do, and nothing is lost:
      // a tour nobody is signed in for cannot have been "seen" by anyone either.
      if (!userId || !currentPreferences) return

      const nextPreferences = {
        ...currentPreferences,
        tours: { ...(currentPreferences.tours ?? {}), [tourId]: record }
      }

      /*
        Fire-and-forget. The tour has already closed by the time this runs, so a failed write must
        not surface to the user — the only cost is that the tour may auto-start once more.
      */
      void updateUser({
        variables: { input: { user_id: userId, preferences: nextPreferences } },
        // Tour state is worth nothing if it only lands in an offline queue: the next session reads
        // it from appDocs, which comes from the server.
        context: { onlineOnly: true },
        update(cache, { data }) {
          const appDocs = cache.readQuery<AppDocsQuery>({ query: AppDocsDocument })
          if (!appDocs?.appDocs || !data?.updateUser) return

          cache.writeQuery<AppDocsQuery>({
            query: AppDocsDocument,
            data: { ...appDocs, appDocs: { ...appDocs.appDocs, preferences: data.updateUser.preferences } }
          })
        }
      }).catch((error) => {
        console.error('AppTourProvider: failed to save tour state', error)
        Sentry.captureException(error, { tags: { source: 'AppTourProvider', tourId } })
      })
    },
    [updateUser]
  )

  const state = useMemo<TourStateAdapter>(() => ({ read, write }), [read, write])

  const onEvent = useCallback((event: TourEvent) => {
    const { name, ...properties } = event
    // One event name per lifecycle point, so PostHog can build a funnel without a property filter.
    track(`tour:${name}`, properties)
  }, [])

  return (
    <TourProvider state={state} onEvent={onEvent}>
      {children}
    </TourProvider>
  )
}
